import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { BANK_SENDER_DOMAINS, GMAIL_FROM_FILTER } from "../lib/bankSenders";
import { CopyButton } from "../lib/CopyButton";
import { SIGNOFF_HANDLE, SIGNOFF_URL } from "../lib/SignOff";
import { AccountLink, refreshSignedIn, useHomePath } from "../lib/session";
import { CheckIcon, Logo } from "../lib/ui";
import { SignupProgress } from "../lib/signupProgress";
import { PhoneStep } from "./PhoneStep";
import { AwaitingStep, ByHandStep, type ApprovalReason } from "./ApprovalSteps";
import type { ProxyType } from "../lib/types";

type Step = 1 | 2 | 3 | 4 | "done";
type Client = "gmail" | "outlook";

interface Created {
  orgId: string;
  inboundAddress: string;
  slug: string;
  persistentLink: string;
  /** "pending" when a person has to approve the organisation before it can take payments. */
  approval: "approved" | "pending";
}

type Test =
  | { kind: "idle" }
  | { kind: "checking" }
  | { kind: "waiting" }
  | { kind: "received"; sender: string | null };

const STEPS = [
  { title: "About your organisation", why: "Who gets paid, and where the money goes." },
  { title: "Check your bank emails you", why: "ConfirmPay reads the emails your bank already sends." },
  { title: "Forward your bank’s emails", why: "So ConfirmPay can see when a payment lands." },
  { title: "Send a test", why: "Check forwarding works, then you are live." },
];

function CopyBox({ text, mono = true, quiet = false }: { text: string; mono?: boolean; quiet?: boolean }) {
  return (
    <div className={"copybox" + (quiet ? " copybox--quiet" : "")}>
      <span className={mono ? "mono" : undefined}>{text}</span>
      <CopyButton text={text} className={"copybtn" + (quiet ? " copybtn--quiet" : "")}>
        Copy
      </CopyButton>
    </div>
  );
}

export default function Onboarding() {
  const home = useHomePath();
  const nav = useNavigate();
  const [step, setStep] = useState<Step>(1);
  const [orgName, setOrgName] = useState("");
  const [email, setEmail] = useState("");
  const [proxyType, setProxyType] = useState<ProxyType>("mobile");
  const [proxyValue, setProxyValue] = useState("");
  const [refPrefix, setRefPrefix] = useState("DON");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [created, setCreated] = useState<Created | null>(null);
  const [client, setClient] = useState<Client>("gmail");
  const [test, setTest] = useState<Test>({ kind: "idle" });
  // Step 2: the three things that must be true before forwarding is worth setting up.
  const [checks, setChecks] = useState({ arrives: false, inbox: false, amount: false });
  const [noEmails, setNoEmails] = useState(false);
  const allChecked = checks.arrives && checks.inbox && checks.amount;

  const emailOk = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim());
  const step1Valid = Boolean(orgName.trim() && emailOk && proxyValue.trim());

  // Whether new organisations can connect at all right now, and whether a
  // mobile number can be confirmed on WhatsApp (the server decides both).
  const [open, setOpen] = useState<boolean | null>(null);
  const [phoneOn, setPhoneOn] = useState(false);
  useEffect(() => {
    let stale = false;
    api.get<{ open: boolean; phoneVerification?: boolean }>("/api/onboarding/open").then((res) => {
      if (stale) return;
      setOpen(res.ok ? res.data.open : true);
      setPhoneOn(res.ok ? Boolean(res.data.phoneVerification) : false);
    });
    return () => {
      stale = true;
    };
  }, []);

  // Step 1 has several parts: the form, the code emailed to the contact address, then (for a
  // mobile number) a message sent from that phone on WhatsApp. The org is only created once those
  // are done; a UEN, or someone who can't use WhatsApp, is checked by hand and waits for approval.
  const [phase, setPhase] = useState<"form" | "code" | "phone" | "byhand" | "awaiting">("form");
  const [pendingReason, setPendingReason] = useState<ApprovalReason>("uen");
  const [code, setCode] = useState("");
  const [note, setNote] = useState("");
  const [resent, setResent] = useState(false);
  // What entering the code earned: good for the address it was entered for.
  const [proof, setProof] = useState<{ email: string; token: string } | null>(null);

  const formFields = () => ({
    name: orgName.trim(),
    email: email.trim(),
    proxyType,
    proxyValue: proxyValue.trim(),
    refPrefix: refPrefix.trim() || "DON",
  });

  type Extra = { phoneProof?: string; requestApproval?: boolean; approvalNote?: string };

  /** Creates the organisation, signed in as it. Either it is approved at once
   *  and sign-up goes on to the bank emails, or it waits for a person. */
  async function createOrg(token: string, extra: Extra = {}, over: { proxyValue?: string } = {}): Promise<boolean> {
    const res = await api.post<Created>("/api/onboarding", {
      ...formFields(),
      ...(over.proxyValue ? { proxyValue: over.proxyValue } : {}),
      emailProof: token,
      ...extra,
    });
    if (!res.ok) {
      setError(res.error);
      return false;
    }
    setCreated(res.data);
    refreshSignedIn(); // creating the org signed this browser in
    if (res.data.approval === "pending") {
      setPendingReason(proxyType === "uen" ? "uen" : extra.requestApproval ? "by-hand" : "number-unconfirmed");
      setPhase("awaiting");
    } else {
      setStep(2);
    }
    return true;
  }

  /** The email is confirmed: on to confirming the number, or to approval. */
  async function afterEmail(token: string) {
    if (proxyType === "uen") {
      // No phone to confirm: the organisation is created and waits for approval.
      if (!(await createOrg(token))) setPhase("form");
      return;
    }
    setError("");
    setPhase(phoneOn ? "phone" : "byhand");
  }

  /** Email the code (or, if this address was already confirmed a moment ago, carry straight on). */
  async function submitForm(e: React.FormEvent, opts: { resend?: boolean } = {}) {
    e.preventDefault();
    if (!step1Valid || busy) return;
    setBusy(true);
    setError("");
    if (proof && proof.email === email.trim().toLowerCase() && !opts.resend) {
      await afterEmail(proof.token);
      setBusy(false);
      return;
    }
    const res = await api.post<{ ok: boolean; devCode?: string }>("/api/onboarding/start", formFields());
    setBusy(false);
    if (!res.ok) {
      setError(res.error);
      return;
    }
    setPhase("code");
    setResent(Boolean(opts.resend));
    setNote(
      res.data.devCode
        ? `Dev mode — your code is ${res.data.devCode}`
        : `We emailed a 6-digit code to ${email.trim()}. It expires in 10 minutes.`,
    );
    setCode(res.data.devCode ?? "");
  }

  async function confirmCode(e: React.FormEvent) {
    e.preventDefault();
    if (code.length !== 6 || busy) return;
    setBusy(true);
    setError("");
    const res = await api.post<{ proof: string }>("/api/auth/verify-email", { email: email.trim(), code });
    if (!res.ok) {
      setBusy(false);
      setError(res.error);
      return;
    }
    setProof({ email: email.trim().toLowerCase(), token: res.data.proof });
    await afterEmail(res.data.proof);
    setBusy(false);
  }

  function backToForm() {
    setPhase("form");
    setCode("");
    setError("");
    setNote("");
    setResent(false);
  }

  const checkConnection = useCallback(async () => {
    if (!created) return;
    setTest({ kind: "checking" });
    const token = created.inboundAddress.split("@")[0] ?? "";
    const res = await api.get<{
      received: boolean;
      lastAt: string | null;
      lastSender: string | null;
    }>(`/api/onboarding/status?token=${encodeURIComponent(token)}`);
    if (res.ok && res.data.received) {
      setTest({ kind: "received", sender: res.data.lastSender ?? null });
    } else {
      setTest({ kind: "waiting" });
    }
  }, [created]);

  // Light auto-poll while waiting for the test email.
  useEffect(() => {
    if (test.kind !== "waiting") return;
    const t = setInterval(checkConnection, 5000);
    return () => clearInterval(t);
  }, [test.kind, checkConnection]);

  const rail = (n: 1 | 2 | 3 | 4) => {
    const at = step === "done" ? 5 : step;
    return at > n ? "done" : at === n ? "active" : "todo";
  };
  const shortLink = created ? created.persistentLink.replace(/^https?:\/\//, "") : "";

  return (
    <div>
      <header className="wrap wrap--setup site-header">
        <Logo height={26} to={home} />
        <AccountLink className="plain-link" style={{ fontSize: 15, fontWeight: 600 }} signedInLabel="Go to your dashboard">
          Already connected? Sign in
        </AccountLink>
      </header>

      <main className="wrap wrap--setup" style={{ paddingTop: 40, paddingBottom: 96 }}>
        <div className="layout">
          {/* progress rail */}
          <aside>
            <h1 className="h1">Connect your organisation</h1>
            <p style={{ margin: "16px 0 40px", fontSize: 17, color: "var(--ink2)" }}>
              About five minutes. No app, no code, no password to hand over.
            </p>
            <ol className="rail" aria-label="Setup progress">
              {STEPS.map((s, i) => {
                const n = (i + 1) as 1 | 2 | 3 | 4;
                const state = rail(n);
                return (
                  <li key={s.title} className={`rail-item rail-item--${state}`} aria-current={state === "active" ? "step" : undefined}>
                    <span className="dot">{state === "done" ? <CheckIcon size={16} strokeWidth={2.6} /> : n}</span>
                    <div>
                      <div className="rt">{s.title}</div>
                      <div className="rd">{s.why}</div>
                    </div>
                  </li>
                );
              })}
            </ol>
          </aside>

          {/* step content */}
          <div className="panel">
            {step === 1 && open === null && (
              <div className="note note--wait" role="status">
                <span className="spin" /> Loading…
              </div>
            )}

            {step === 1 && open === false && (
              <div>
                <h2 className="h1" style={{ fontSize: 30, letterSpacing: "-0.03em" }}>Sign-ups are paused</h2>
                <p style={{ margin: "10px 0 28px", fontSize: 16, color: "var(--ink2)" }}>
                  New organisations can&rsquo;t connect while we finish testing. If yours is already connected, sign in to reach your dashboard.
                </p>
                <Link to="/login" className="btn">Sign in</Link>
              </div>
            )}

            {step === 1 && open === true && phase === "form" && (
              <form onSubmit={(e) => submitForm(e)} noValidate>
                <SignupProgress states={["cur", "todo", "todo"]} third={proxyType === "uen" ? "Approval" : "Number"} />
                <h2 className="h1" style={{ fontSize: 30, letterSpacing: "-0.03em" }}>About your organisation</h2>
                <p style={{ margin: "10px 0 30px", fontSize: 16, color: "var(--ink2)" }}>
                  This is what payers see, and where their money goes directly. ConfirmPay never touches it.
                </p>

                <div className="field">
                  <label className="lbl" htmlFor="su-name">Organisation or business name</label>
                  <input id="su-name" className="inp" maxLength={25} autoComplete="organization" value={orgName} onChange={(e) => setOrgName(e.target.value)} placeholder="e.g. Kampong Cares" />
                  <p className="hint">Shown on the PayNow QR your payers scan. 25 characters max.</p>
                </div>

                <div className="field">
                  <label className="lbl" htmlFor="su-email">Contact email</label>
                  <input id="su-email" className="inp" type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@organisation.org" />
                  <p className="hint">We will email a code here to confirm it is yours. You will also use it to sign in. Not shown to payers.</p>
                </div>

                <div className="field">
                  <span className="lbl" id="su-type">Paying into</span>
                  <div className="seg" role="group" aria-labelledby="su-type">
                    <button type="button" aria-pressed={proxyType === "mobile"} onClick={() => setProxyType("mobile")}>PayNow mobile number</button>
                    <button type="button" aria-pressed={proxyType === "uen"} onClick={() => setProxyType("uen")}>Business UEN</button>
                  </div>
                </div>

                <div className="field">
                  <label className="lbl" htmlFor="su-proxy">{proxyType === "uen" ? "UEN" : "PayNow mobile number"}</label>
                  <input
                    id="su-proxy"
                    className="inp inp--mono"
                    inputMode={proxyType === "uen" ? "text" : "tel"}
                    value={proxyValue}
                    onChange={(e) => setProxyValue(e.target.value)}
                    placeholder={proxyType === "uen" ? "e.g. 201234567A" : "e.g. 9123 4567"}
                  />
                  <p className="hint">Payers pay this directly, bank to bank.</p>
                </div>

                <div className="field">
                  <label className="lbl" htmlFor="su-prefix">Reference prefix</label>
                  <input id="su-prefix" className="inp inp--mono" style={{ maxWidth: 200 }} maxLength={8} value={refPrefix} onChange={(e) => setRefPrefix(e.target.value.toUpperCase())} placeholder="DON" />
                  <p className="hint">
                    Every payment gets a reference like <span className="mono">{(refPrefix.trim() || "DON")}-001</span>. Up to 8 characters.
                  </p>
                </div>

                {error && <div className="error-box" role="alert">{error}</div>}
                <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 34 }}>
                  <button className="btn" disabled={!step1Valid || busy}>
                    {busy ? "Working…" : "Continue"}
                  </button>
                </div>
                <p className="lg-consent" style={{ textAlign: "right" }}>
                  By continuing you agree to the <Link to="/terms" target="_blank">Terms</Link> and the <Link to="/privacy" target="_blank">Privacy Policy</Link>.
                </p>
              </form>
            )}

            {step === 1 && open === true && phase === "code" && (
              <form onSubmit={confirmCode} noValidate>
                <SignupProgress states={["done", "cur", "todo"]} third={proxyType === "uen" ? "Approval" : "Number"} />
                <h2 className="h1" style={{ fontSize: 30, letterSpacing: "-0.03em" }}>Check your email</h2>
                <p style={{ margin: "10px 0 24px", fontSize: 16, color: "var(--ink2)" }}>{note}</p>
                {resent && (
                  <div className="note note--ok" role="status" style={{ marginTop: 0, marginBottom: 20, fontSize: 14.5 }}>
                    <CheckIcon size={18} />
                    A new code is on its way.
                  </div>
                )}
                <div className="field">
                  <label className="lbl" htmlFor="su-code">6-digit code</label>
                  <input
                    id="su-code"
                    className="inp codein"
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    autoComplete="one-time-code"
                    maxLength={6}
                    autoFocus
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                    placeholder="000000"
                  />
                  <p className="hint">Nothing is set up until the code is entered.</p>
                </div>
                {error && <div className="error-box" role="alert">{error}</div>}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 34, gap: 16, flexWrap: "wrap" }}>
                  <div style={{ display: "flex", gap: 22 }}>
                    <button type="button" className="textbtn" style={{ fontSize: 15 }} onClick={backToForm}>
                      Use a different email
                    </button>
                    <button type="button" className="textbtn" style={{ fontSize: 15 }} disabled={busy} onClick={(e) => submitForm(e, { resend: true })}>
                      Resend code
                    </button>
                  </div>
                  <button className="btn" disabled={code.length !== 6 || busy}>
                    {busy ? "Connecting…" : "Confirm and continue"}
                  </button>
                </div>
              </form>
            )}

            {step === 1 && open === true && phase === "phone" && proof && (
              <PhoneStep
                form={{ ...formFields(), proxyType: "mobile" }}
                emailProof={proof.token}
                onChangeNumber={() => {
                  setError("");
                  setPhase("form");
                }}
                onByHand={() => {
                  setError("");
                  setPhase("byhand");
                }}
                onContinue={async ({ proof: phoneProof, number }) => {
                  setError("");
                  // The number confirmed may differ from the one typed ("my PayNow
                  // number is the one I just sent from"), so it is the one connected.
                  if (number !== proxyValue.trim()) setProxyValue(number);
                  if (!(await createOrg(proof.token, { phoneProof }, { proxyValue: number }))) setPhase("form");
                }}
              />
            )}

            {step === 1 && open === true && phase === "byhand" && proof && (
              <ByHandStep
                form={formFields()}
                canTryWhatsApp={phoneOn && proxyType === "mobile"}
                busy={busy}
                error={error}
                onSubmit={async (approvalNote) => {
                  setBusy(true);
                  setError("");
                  await createOrg(proof.token, { requestApproval: true, approvalNote });
                  setBusy(false);
                }}
                onTryWhatsApp={() => {
                  setError("");
                  setPhase("phone");
                }}
                onBackToDetails={() => {
                  setError("");
                  setPhase("form");
                }}
              />
            )}

            {step === 1 && open === true && phase === "awaiting" && (
              <AwaitingStep
                form={formFields()}
                reason={pendingReason}
                onDashboard={() => nav("/dashboard")}
                onSetUpEmails={() => setStep(2)}
              />
            )}

            {step === 2 && created && (
              <>
                <h2 className="h1" style={{ fontSize: 30, letterSpacing: "-0.03em" }}>Check your bank emails you</h2>
                <p style={{ margin: "10px 0 28px", fontSize: 16, color: "var(--ink2)" }}>
                  ConfirmPay confirms payments from the emails your bank already sends when money arrives. It can&rsquo;t switch those emails on for you, so make sure they are coming before you set up forwarding.
                </p>

                <fieldset className="checklist">
                  <legend>Tick each one once it is true</legend>
                  <label className="checkrow">
                    <input type="checkbox" checked={checks.arrives} onChange={(e) => setChecks((c) => ({ ...c, arrives: e.target.checked }))} />
                    <span>My bank emails me whenever a PayNow payment arrives in the account linked to <span className="mono">{proxyValue.trim()}</span>.</span>
                  </label>
                  <label className="checkrow">
                    <input type="checkbox" checked={checks.inbox} onChange={(e) => setChecks((c) => ({ ...c, inbox: e.target.checked }))} />
                    <span>I can find those emails in the Gmail or Outlook inbox I will forward from.</span>
                  </label>
                  <label className="checkrow">
                    <input type="checkbox" checked={checks.amount} onChange={(e) => setChecks((c) => ({ ...c, amount: e.target.checked }))} />
                    <span>Each email shows how much was paid.</span>
                  </label>
                </fieldset>

                <p className="hint" style={{ marginTop: 16 }}>
                  ConfirmPay can read emails from {BANK_SENDER_DOMAINS.map((b) => b.label).join(", ")}.
                </p>

                <div style={{ marginTop: 22 }}>
                  <button
                    type="button"
                    className="textbtn"
                    style={{ fontSize: 15 }}
                    aria-expanded={noEmails}
                    aria-controls="no-emails"
                    onClick={() => setNoEmails((v) => !v)}
                  >
                    My bank doesn&rsquo;t email me yet
                  </button>
                </div>
                {noEmails && (
                  <div id="no-emails" className="helpbox">
                    <strong>Switch them on first.</strong>
                    <p>
                      In your bank&rsquo;s app or internet banking, look for notification or alert settings and turn on email alerts for money received or PayNow payments in. Banks word this differently, and some only offer it on certain accounts. Then ask someone to send you a small amount and check that the email arrives.
                    </p>
                    <p>
                      Talk to{" "}
                      <a href={SIGNOFF_URL} target="_blank" rel="noopener noreferrer">{SIGNOFF_HANDLE}</a>{" "}
                      if you are having difficulties.
                    </p>
                  </div>
                )}

                <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 34 }}>
                  <button type="button" className="btn" disabled={!allChecked} onClick={() => setStep(3)}>Continue</button>
                </div>
              </>
            )}

            {step === 3 && created && (
              <>
                <h2 className="h1" style={{ fontSize: 30, letterSpacing: "-0.03em" }}>Forward your bank&rsquo;s emails</h2>
                <p style={{ margin: "10px 0 28px", fontSize: 16, color: "var(--ink2)" }}>
                  Your bank already emails you when money arrives. Send a copy to ConfirmPay with one filter in your own inbox. You can delete it yourself at any time.
                </p>

                <span className="lbl">Your private forwarding address</span>
                <CopyBox text={created.inboundAddress} />
                <p className="hint">Not a mailbox. Nothing is stored here; mail sent to it just triggers the code that reads the payment.</p>

                <div className="tabs" role="group" aria-label="Email provider" style={{ marginTop: 34 }}>
                  <button type="button" className="tab" aria-pressed={client === "gmail"} onClick={() => setClient("gmail")}>Gmail</button>
                  <button type="button" className="tab" aria-pressed={client === "outlook"} onClick={() => setClient("outlook")}>Outlook / Microsoft 365</button>
                </div>

                {client === "gmail" ? (
                  <ol className="steps">
                    <li><span>Open Settings, then <code>Filters and Blocked Addresses</code>, then <code>Create a new filter</code>.</span></li>
                    <li>
                      <span>
                        In <code>From</code>, paste this list of bank senders:
                        <span style={{ display: "block", marginTop: 10 }}>
                          <CopyBox text={GMAIL_FROM_FILTER} quiet />
                        </span>
                      </span>
                    </li>
                    <li><span>Choose <code>Create filter</code>, tick <code>Forward it to</code>, then <code>add forwarding address</code> and paste your private address above.</span></li>
                    <li><span>Gmail sends a one-time confirmation to that address. ConfirmPay accepts it for you. Wait a minute, then finish creating the filter.</span></li>
                  </ol>
                ) : (
                  <ol className="steps">
                    <li><span>Open Settings, then <code>Mail</code>, then <code>Rules</code>, then <code>Add new rule</code>.</span></li>
                    <li><span>Set the condition to <code>From</code> and enter your bank&rsquo;s notification address. Add one rule for each bank you use.</span></li>
                    <li><span>Set the action to <code>Forward to</code> and paste your private address above.</span></li>
                    <li><span>Outlook may send a verification message first. ConfirmPay accepts it for you.</span></li>
                  </ol>
                )}

                <p style={{ margin: "28px 0 0", paddingTop: 20, borderTop: "1px solid var(--line)", fontSize: 14, color: "var(--ink2)", lineHeight: 1.6 }}>
                  ConfirmPay can read emails from {BANK_SENDER_DOMAINS.map((b) => b.label).join(", ")}. Another bank? Forward it anyway and mark payments paid by hand from your dashboard.
                </p>

                <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 34 }}>
                  <button type="button" className="btn" onClick={() => { setTest({ kind: "idle" }); setStep(4); }}>Continue</button>
                </div>
              </>
            )}

            {step === 4 && created && (
              <>
                <h2 className="h1" style={{ fontSize: 30, letterSpacing: "-0.03em" }}>Send a test</h2>
                <p style={{ margin: "10px 0 28px", fontSize: 16, color: "var(--ink2)" }}>
                  Email anything to your private address, through the new filter or straight from your inbox, then check. Nothing is marked paid by a test.
                </p>
                <CopyBox text={created.inboundAddress} />

                <div style={{ marginTop: 22 }}>
                  <button type="button" className="btn" onClick={checkConnection} disabled={test.kind === "checking"}>
                    {test.kind === "checking" ? "Checking…" : "Check now"}
                  </button>
                </div>

                <div role="status">
                  {test.kind === "waiting" && (
                    <div className="note note--wait"><span className="spin" />Nothing has arrived yet. Still checking.</div>
                  )}
                  {test.kind === "received" && (
                    <div className="note note--ok">
                      <CheckIcon size={20} />
                      Received a forwarded email{test.sender ? ` from ${test.sender}` : ""}.
                    </div>
                  )}
                </div>

                <div style={{ display: "flex", justifyContent: "space-between", marginTop: 34 }}>
                  <button type="button" className="btn btn--ghost" onClick={() => setStep(3)}>Back</button>
                  <button type="button" className="btn" onClick={() => setStep("done")}>
                    {test.kind === "received" ? "Finish setup" : "Finish anyway"}
                  </button>
                </div>
              </>
            )}

            {step === "done" && created && (
              <>
                <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                  <span style={{ width: 48, height: 48, borderRadius: "50%", background: "var(--sage)", color: "#0e1007", display: "grid", placeItems: "center", flex: "none" }}>
                    <CheckIcon size={24} />
                  </span>
                  <h2 className="h1" style={{ fontSize: 32, letterSpacing: "-0.03em" }}>You are connected</h2>
                </div>
                <p style={{ margin: "14px 0 28px", fontSize: 16, color: "var(--ink2)" }}>
                  You are signed in on this device. Next time, sign in with your contact email and a one-time code.
                </p>

                <div style={{ border: "1.5px solid var(--ink)", borderRadius: 8, padding: "20px 22px", background: "var(--paper)" }}>
                  <span className="lbl" style={{ marginBottom: 8 }}>Your public payment link</span>
                  <CopyBox text={shortLink} quiet />
                  <p className="hint">Put it on a poster or in a group chat. Supporters enter their own amount, pay, and you see it land.</p>
                </div>

                <div className="kv" style={{ marginTop: 22 }}>
                  <div className="row"><span>Organisation</span><span>{orgName.trim()}</span></div>
                  <div className="row"><span>Contact email</span><span>{email.trim()}</span></div>
                  <div className="row"><span>PayNow</span><span className="mono">{proxyValue.trim()}</span></div>
                  <div className="row"><span>Forwarding address</span><span className="mono" style={{ fontSize: 14 }}>{created.inboundAddress}</span></div>
                </div>

                <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 30 }}>
                  <Link to="/dashboard" className="btn">Go to dashboard</Link>
                </div>
              </>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
